import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { salesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Search, Receipt, Calendar as CalendarIcon, Eye, Printer, Trash2, TrendingUp, X, CreditCard, Banknote, Smartphone } from 'lucide-react';
import { cn } from '../lib/utils';

const PAYMENT_BADGE = {
    cash: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    card: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
    stripe: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
    helcim: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
};

const PAYMENT_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'cash', label: 'Cash', icon: Banknote },
    { key: 'card', label: 'Card', icon: CreditCard },
    { key: 'other', label: 'Other', icon: Smartphone },
];

export default function Sales() {
    const { isAdmin, isManager } = useAuth();
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterPayment, setFilterPayment] = useState('all');
    const [dateRange, setDateRange] = useState({ from: null, to: null });
    const [selectedSale, setSelectedSale] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const isMobile = window.innerWidth < 768;

    useEffect(() => {
        fetchSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange]);

    const fetchSales = async () => {
        setLoading(true);
        try {
            const params = {};
            if (dateRange.from) params.start_date = dateRange.from.toISOString();
            if (dateRange.to) params.end_date = dateRange.to.toISOString();
            const response = await salesAPI.getAll(params);
            setSales(response.data);
        } catch { toast.error('Failed to load sales'); }
        finally { setLoading(false); }
    };

    const filteredSales = sales.filter(s => {
        const matchSearch = s.receipt_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
            s.cashier_name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchPayment = filterPayment === 'all'
            || (filterPayment === 'other' ? (s.payment_method !== 'cash' && s.payment_method !== 'card')
                : s.payment_method === filterPayment);
        return matchSearch && matchPayment;
    });

    const totalRevenue = filteredSales.reduce((sum, s) => sum + s.total, 0);
    const avgSale = filteredSales.length > 0 ? totalRevenue / filteredSales.length : 0;
    const canDelete = isAdmin || isManager;

    const handleDeleteSale = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await salesAPI.delete(deleteTarget.id);
            setSales(prev => prev.filter(s => s.id !== deleteTarget.id));
            toast.success(`Sale ${deleteTarget.receipt_number} deleted`);
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete sale'); }
        finally { setDeleting(false); setDeleteTarget(null); }
    };

    // Payment breakdown data
    const cashTotal = filteredSales.filter(s => s.payment_method === 'cash').reduce((s, t) => s + t.total, 0);
    const cardTotal = filteredSales.filter(s => s.payment_method === 'card').reduce((s, t) => s + t.total, 0);
    const otherTotal = filteredSales.filter(s => s.payment_method !== 'cash' && s.payment_method !== 'card').reduce((s, t) => s + t.total, 0);

    return (
        <Layout title="Sales">
            <div className="space-y-5" data-testid="sales-page">

                {/* ── Stats ── */}
                <div className="grid grid-cols-3 gap-3">
                    <div className="stat-card border-l-4 border-l-emerald-500">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="stat-card-icon bg-emerald-500/10 shrink-0"><Receipt className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400" /></div>
                            <div>
                                <p className="text-lg sm:text-2xl font-black font-mono">${totalRevenue.toFixed(0)}</p>
                                <p className="text-[10px] sm:text-xs text-muted-foreground font-medium">Revenue</p>
                            </div>
                        </div>
                    </div>
                    <div className="stat-card border-l-4 border-l-cyan-500">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="stat-card-icon bg-cyan-500/10 shrink-0"><TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-600 dark:text-cyan-400" /></div>
                            <div>
                                <p className="text-lg sm:text-2xl font-black font-mono">{filteredSales.length}</p>
                                <p className="text-[10px] sm:text-xs text-muted-foreground font-medium">Transactions</p>
                            </div>
                        </div>
                    </div>
                    <div className="stat-card border-l-4 border-l-violet-500">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="stat-card-icon bg-violet-500/10 shrink-0"><TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-violet-600 dark:text-violet-400" /></div>
                            <div>
                                <p className="text-lg sm:text-2xl font-black font-mono">${avgSale.toFixed(0)}</p>
                                <p className="text-[10px] sm:text-xs text-muted-foreground font-medium">Avg Sale</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Payment Breakdown ── */}
                {filteredSales.length > 0 && (
                    <Card className="shadow-sm">
                        <CardContent className="p-4">
                            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Payment Breakdown</p>
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { label: 'Cash', icon: Banknote, total: cashTotal, color: 'text-emerald-500', bar: 'bg-emerald-500' },
                                    { label: 'Card', icon: CreditCard, total: cardTotal, color: 'text-blue-500', bar: 'bg-blue-500' },
                                    { label: 'Other', icon: Smartphone, total: otherTotal, color: 'text-violet-500', bar: 'bg-violet-500' },
                                ].map(({ label, icon: Icon, total, color, bar }) => (
                                    <div key={label} className="space-y-1.5">
                                        <div className="flex items-center gap-1.5">
                                            <Icon className={`h-3.5 w-3.5 ${color} shrink-0`} />
                                            <span className="text-xs font-semibold">{label}</span>
                                        </div>
                                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                            <div className={`h-full ${bar} rounded-full`} style={{ width: totalRevenue > 0 ? `${(total / totalRevenue) * 100}%` : '0%' }} />
                                        </div>
                                        <p className="font-mono font-bold text-xs">${total.toFixed(2)}</p>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* ── Filters ── */}
                <div className="space-y-3">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input placeholder="Search receipt or cashier..." className="pl-9" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="sales-search" />
                        </div>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className={cn('shrink-0', !dateRange.from && 'text-muted-foreground')} data-testid="date-filter-btn">
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    <span className="hidden sm:inline">
                                        {dateRange.from ? (
                                            dateRange.to ? `${format(dateRange.from, 'MMM d')} – ${format(dateRange.to, 'MMM d')}` : format(dateRange.from, 'MMM d')
                                        ) : 'Date'}
                                    </span>
                                    <span className="sm:hidden">
                                        {dateRange.from ? format(dateRange.from, 'M/d') : 'Date'}
                                    </span>
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="end">
                                <Calendar mode="range" selected={dateRange} onSelect={setDateRange} numberOfMonths={isMobile ? 1 : 2} />
                            </PopoverContent>
                        </Popover>
                        {dateRange.from && (
                            <Button variant="ghost" size="icon" onClick={() => setDateRange({ from: null, to: null })} data-testid="clear-date-btn">
                                <X className="h-4 w-4" />
                            </Button>
                        )}
                    </div>

                    {/* Payment filter chips */}
                    <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                        {PAYMENT_FILTERS.map(({ key, label, icon: Icon }) => (
                            <button
                                key={key}
                                onClick={() => setFilterPayment(key)}
                                className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                                    filterPayment === key
                                        ? 'bg-cyan-500 text-white border-cyan-500'
                                        : 'border-border text-muted-foreground hover:border-cyan-500/40 bg-muted/40'
                                }`}
                            >
                                {Icon && <Icon className="h-3.5 w-3.5" />}
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Mobile Card List ── */}
                {isMobile ? (
                    <div className="space-y-2">
                        {loading ? (
                            [...Array(5)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />)
                        ) : filteredSales.length === 0 ? (
                            <div className="py-16 text-center">
                                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-muted flex items-center justify-center"><Receipt className="h-5 w-5 text-muted-foreground" /></div>
                                <p className="font-semibold text-muted-foreground">No sales found</p>
                            </div>
                        ) : filteredSales.map((sale) => (
                            <div
                                key={sale.id}
                                className="bg-card border rounded-xl p-4 flex items-center gap-3 cursor-pointer active:bg-muted/50 transition-colors"
                                onClick={() => { setSelectedSale(sale); setDetailOpen(true); }}
                                data-testid={`sale-row-${sale.id}`}
                            >
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${PAYMENT_BADGE[sale.payment_method] || 'bg-muted'}`}>
                                    {sale.payment_method === 'cash' ? <Banknote className="h-5 w-5" /> :
                                     sale.payment_method === 'card' ? <CreditCard className="h-5 w-5" /> :
                                     <Smartphone className="h-5 w-5" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="font-mono text-xs text-muted-foreground truncate">{sale.receipt_number}</span>
                                        <span className="font-black font-mono text-base shrink-0">${sale.total.toFixed(2)}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 mt-0.5">
                                        <span className="text-xs text-muted-foreground">{sale.items.length} items · {sale.cashier_name}</span>
                                        <span className="text-[10px] text-muted-foreground">{new Date(sale.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    /* ── Desktop Table ── */
                    <Card className="shadow-sm overflow-hidden">
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                                        <TableHead className="font-semibold">Receipt #</TableHead>
                                        <TableHead className="font-semibold">Date & Time</TableHead>
                                        <TableHead className="font-semibold">Items</TableHead>
                                        <TableHead className="font-semibold">Payment</TableHead>
                                        <TableHead className="text-right font-semibold">Total</TableHead>
                                        <TableHead className="font-semibold">Cashier</TableHead>
                                        <TableHead className="w-[80px]" />
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        [...Array(5)].map((_, i) => (
                                            <TableRow key={i}><TableCell colSpan={7}><div className="h-10 bg-muted/50 rounded animate-pulse" /></TableCell></TableRow>
                                        ))
                                    ) : filteredSales.map((sale) => (
                                        <TableRow key={sale.id} className="group cursor-pointer hover:bg-muted/30" onClick={() => { setSelectedSale(sale); setDetailOpen(true); }} data-testid={`sale-row-${sale.id}`}>
                                            <TableCell><span className="font-mono text-xs bg-muted px-2 py-1 rounded-md">{sale.receipt_number}</span></TableCell>
                                            <TableCell className="text-muted-foreground text-sm">{new Date(sale.created_at).toLocaleString()}</TableCell>
                                            <TableCell><span className="text-sm font-medium">{sale.items.length} items</span></TableCell>
                                            <TableCell>
                                                <Badge className={cn('capitalize text-xs border', PAYMENT_BADGE[sale.payment_method] || 'bg-muted')} variant="outline">
                                                    {sale.payment_method}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right font-mono font-black text-base">${sale.total.toFixed(2)}</TableCell>
                                            <TableCell className="text-muted-foreground text-sm">{sale.cashier_name}</TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedSale(sale); setDetailOpen(true); }} data-testid={`view-sale-${sale.id}`}>
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    {canDelete && (
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setDeleteTarget(sale)} data-testid={`delete-sale-${sale.id}`}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {!loading && filteredSales.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} className="py-16 text-center">
                                                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-muted flex items-center justify-center"><Receipt className="h-5 w-5 text-muted-foreground" /></div>
                                                <p className="font-semibold text-muted-foreground">No sales found</p>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* ── Sale Detail Dialog ── */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid="sale-detail-dialog">
                    {selectedSale && (
                        <div>
                            <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white text-center py-5 px-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span />
                                    <Button variant="ghost" size="icon" className="text-white/60 hover:text-white hover:bg-white/10 h-8 w-8" onClick={() => window.print()} data-testid="print-receipt-btn">
                                        <Printer className="h-4 w-4" />
                                    </Button>
                                </div>
                                <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-cyan-500 flex items-center justify-center"><span className="font-black text-lg">M</span></div>
                                <h3 className="font-black text-base">MEZGEB POS</h3>
                                <p className="text-slate-400 text-xs mt-1">{selectedSale.receipt_number}</p>
                                <p className="text-slate-400 text-xs">{new Date(selectedSale.created_at).toLocaleString()}</p>
                            </div>
                            <div className="p-5 space-y-4 font-mono text-sm">
                                <div className="space-y-2">
                                    {selectedSale.items.map((item, idx) => (
                                        <div key={idx} className="flex justify-between">
                                            <span className="font-sans font-medium">{item.quantity > 1 ? `${item.quantity}x ` : ''}{item.product_name}</span>
                                            <span className="font-semibold">${item.subtotal.toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                                <Separator className="border-dashed" />
                                <div className="space-y-1 text-sm">
                                    <div className="flex justify-between text-muted-foreground"><span className="font-sans">Subtotal</span><span>${selectedSale.subtotal.toFixed(2)}</span></div>
                                    <div className="flex justify-between text-muted-foreground"><span className="font-sans">Tax</span><span>${((selectedSale.state_tax || 0) + (selectedSale.county_tax || 0)).toFixed(2)}</span></div>
                                    {(selectedSale.tip || 0) > 0 && <div className="flex justify-between text-muted-foreground"><span className="font-sans">Tip</span><span>${selectedSale.tip.toFixed(2)}</span></div>}
                                    {(selectedSale.card_fee || 0) > 0 && <div className="flex justify-between text-amber-600"><span className="font-sans">Card Fee</span><span>${selectedSale.card_fee.toFixed(2)}</span></div>}
                                    {selectedSale.discount > 0 && <div className="flex justify-between text-emerald-600"><span className="font-sans">Discount</span><span>-${selectedSale.discount.toFixed(2)}</span></div>}
                                </div>
                                <Separator className="border-dashed" />
                                <div className="flex justify-between items-baseline"><span className="font-sans font-black text-lg">Total</span><span className="font-black text-2xl text-cyan-600">${selectedSale.total.toFixed(2)}</span></div>
                                <Separator className="border-dashed" />
                                <div className="text-xs text-muted-foreground">
                                    <div className="flex justify-between"><span className="font-sans uppercase font-semibold">{selectedSale.payment_method}</span><span>${selectedSale.amount_paid.toFixed(2)}</span></div>
                                    {selectedSale.change_given > 0 && <div className="flex justify-between mt-0.5"><span className="font-sans">Change</span><span>${selectedSale.change_given.toFixed(2)}</span></div>}
                                    <p className="font-sans mt-2">Cashier: {selectedSale.cashier_name}</p>
                                </div>
                                {canDelete && (
                                    <div className="pt-2">
                                        <Button variant="outline" size="sm" className="w-full text-destructive hover:text-destructive border-destructive/30"
                                            onClick={() => { setDetailOpen(false); setDeleteTarget(selectedSale); }}>
                                            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete Sale
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* ── Delete Confirmation ── */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="delete-sale-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Sale Record</AlertDialogTitle>
                        <AlertDialogDescription>
                            Delete sale <span className="font-mono font-semibold">{deleteTarget?.receipt_number}</span>? This will restore stock for all items. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting} data-testid="cancel-delete-sale">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteSale} disabled={deleting} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground" data-testid="confirm-delete-sale">
                            {deleting ? 'Deleting...' : 'Delete Sale'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
